import { ApexOptions } from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import { dataToSeries, dateToSeriesTime } from '../../utility'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import { currency } from 'src/feature/bite/helpers/currency'

export default function TimeLineChart({
  revenue,
  revenueTimeBand,
}: {
  revenue?: {
    dailyRevenueChartData: {
      date: string
      value: number
    }[]
  }
  revenueTimeBand?: {
    revenueTimeBandChartData: {
      period: number
      revenue: number
      percentageValue: number
    }[]
  }
}) {
  const intl = useIntl()

  const revenueTimeBandData = dateToSeriesTime(
    // 'Revenue Time Band,
    intl.formatMessage({ id: 'ANALYTICS.REVENUE_TIME_BAND' }),
    revenueTimeBand?.revenueTimeBandChartData || []
  )
  const dailyRevenueData = dataToSeries(
    // 'Daily Revenue',
    intl.formatMessage({ id: 'ANALYTICS.DAILY_REVENUE' }),
    revenue?.dailyRevenueChartData || []
  )

  const revenueTimeBandSeries = revenueTimeBandData.series.map((item) => {
    return {
      ...item,
      data: revenueTimeBandData.percentages.map((val) => (val < 10 ? 15 : val)),
    }
  })

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: ['#fff'],
      },
      formatter: function (value, { seriesIndex, dataPointIndex, w }) {
        let percentageVal = revenueTimeBandData.percentages[dataPointIndex]
        // if (percentageVal < 5) percentageVal = 5
        // return '$' + Number(value)?.toFixed(2) + ' (' + percentageVal + '%)'
        return `${currency(Number(value) || 0)} (${percentageVal}%)`
      },
    },
    colors: [
      function ({ value, seriesIndex, w }) {
        const highestValue = Math.max(...w.config.series[0].data)
        if (value === highestValue) return '#5b0b93'
        else return '#888'
      },
    ],
    tooltip: {
      shared: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        return (
          '<div class="arrow_box p-3"><b>Revenue: </b><span>' +
          series[seriesIndex][dataPointIndex] +
          '</span><br/><b>Percentage: </b><span>' +
          revenueTimeBandData.percentages[dataPointIndex] +
          '%' +
          '</span></div>'
        )
      },
    },
    xaxis: {
      // show: false,
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
      categories: ['12:00 PM - 05:59 PM', '06:00 PM - 11:59 PM', '12:00 AM - 11:59 AM'],
    },
    yaxis: {
      min: -5,
    },
    grid: {
      show: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
      },
    },
  }

  const revenueMax = dailyRevenueData.series.reduce((acc, curr) => {
    return Math.max(acc, ...curr.data)
  }, 0)

  const optionsRight: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
    },
    grid: {
      show: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        // borderRadiusApplication: 'circular',
        distributed: true,
        barHeight: '80%',
        isFunnel: false,
        dataLabels: {
          position: 'top',
          total: {
            offsetY: -30,
          },
        },
      },
    },
    colors: [
      function ({ value, seriesIndex, w }) {
        const highestValue = Math.max(...w.config.series[0].data)
        if (value === highestValue) return '#5b0b93'
        else return '#888'
      },
    ],
    dataLabels: {
      enabled: true,
      offsetY: -30,
      style: {
        colors: ['#333'],
      },
      formatter: function (value, { seriesIndex, dataPointIndex, w }) {
        return currency((value as number) || 0)
      },
    },
    yaxis: {
      // %10 of revenueMax
      max: revenueMax + revenueMax * 0.1,
      show: true,
      axisBorder: {
        show: false,
        color: '#333',
      },
      labels: {
        show: false,
        style: {
          colors: '#888',
          fontSize: '12px',
        },
      },
    },
    xaxis: {
      categories: dailyRevenueData.categories,
      labels: {
        formatter: function (value) {
          return moment(value).format('DD MMM')
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    legend: {
      show: false,
    },
  }
  return (
    <div>
      <div className='separator my-10' style={{ borderBottom: '1px solid #e0e0e0' }}></div>
      <div className='d-flex justify-content-between gap-5 mt-5'>
        <div className='w-100 d-flex gap-5'>
          <div className='card w-100'>
            <div className='card-header position-relative w-100 d-flex justify-content-center align-items-center border-0'>
              <h5 className='fs-1 fw-bold position-absolute'>
                {/* Revenue Time Band */}
                {intl.formatMessage({ id: 'ANALYTICS.REVENUE_TIME_BAND' })}
                <span className='position-relative z-50'>
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip>
                        {intl.formatMessage({ id: 'ANALYTICS.REVENUE_TIME_BAND_TOOLTIP' })}
                      </Tooltip>
                    }
                  >
                    <i className='bi bi-info-circle text-primary ms-1'></i>
                  </OverlayTrigger>
                </span>
              </h5>
            </div>
            <div id='timeline-chart'>
              {revenueTimeBandSeries[0].data.length === 0 ? (
                <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner animation='border' /></div>
              ) : (
                <ReactApexChart
                  options={options}
                  series={revenueTimeBandSeries}
                  type='bar'
                  height={200}
                />
              )}
            </div>
          </div>
          <div className='card w-100'>
            <div className='card-header position-relative w-100 d-flex justify-content-center align-items-center border-0'>
              <h5 className='fs-1 fw-bold position-absolute'>
                {/* Daily Revenue ($) */}
                {intl.formatMessage({ id: 'ANALYTICS.DAILY_REVENUE' })}
                <span className='position-relative z-50'>
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip>
                        {intl.formatMessage({ id: 'ANALYTICS.DAILY_REVENUE_TOOLTIP' })}
                      </Tooltip>
                    }
                  >
                    <i className='bi bi-info-circle text-primary ms-1'></i>
                  </OverlayTrigger>
                </span>
              </h5>
            </div>
            <div id='timeline-chart'>
              {dailyRevenueData.series[0].data.length === 0 ? (
                <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Spinner animation='border' />
                </div>
              ) : (
                <ReactApexChart
                  options={optionsRight}
                  series={dailyRevenueData.series}
                  type='bar'
                  height={200}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
